import { Card, CardBody } from "@heroui/react";
import React from "react";

/**
 * Mention autocomplete dropdown component
 * @param {object} props Component props
 * @param {Array<{userId: string, displayName: string}>} props.members List of team members
 * @param {string} props.query Current search query (text after @)
 * @param {(member: {userId: string, displayName: string}) => void} props.onSelect Callback when a member is selected
 * @param {() => void} props.onClose Callback to close the dropdown
 * @returns {React.ReactElement|null} Autocomplete dropdown
 */
export default function MentionAutocomplete({
  members,
  query,
  onSelect,
  onClose,
}) {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const containerRef = React.useRef(null);

  const filteredMembers = React.useMemo(() => {
    if (!query) return members;
    const lowerQuery = query.toLowerCase();
    return members.filter((m) =>
      m.displayName.toLowerCase().includes(lowerQuery),
    );
  }, [members, query]);

  React.useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (filteredMembers.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredMembers.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredMembers.length - 1,
        );
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        onSelect(filteredMembers[selectedIndex]);
      }
    };

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filteredMembers, selectedIndex, onSelect, onClose]);

  if (filteredMembers.length === 0) {
    return null;
  }

  return (
    <Card
      ref={containerRef}
      className="absolute bottom-full left-0 z-50 mb-1 w-64 shadow-lg"
    >
      <CardBody className="p-1">
        <ul className="max-h-40 overflow-y-auto">
          {filteredMembers.slice(0, 5).map((member, index) => (
            <li key={member.userId}>
              <button
                type="button"
                className={`w-full rounded px-3 py-2 text-left text-sm transition-colors ${
                  index === selectedIndex
                    ? "bg-primary-100 text-primary"
                    : "hover:bg-default-100"
                }`}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => onSelect(member)}
              >
                @{member.displayName.replace(" ", "")}
              </button>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
